import { formatDateWithTime } from '@longy/common'
import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CheckoutLayout } from '../../components/checkout/CheckoutLayout'
import { AttachedBlocks, useAttachedBlocks } from '../../components/cms/AttachedBlocks'
import { WebinarCtaButton } from '../../components/webinars/WebinarCtaButton'
import { api } from '../../core/trpc'
import { useAuth } from '../../hooks/useAuth'
import { useLocale } from '../../hooks/useLocale'
import { formatPrice } from '../../utils/formatPrice'
import { buildOgMeta } from '../../utils/og'
import { longFormRichTextClassName } from '../../utils/richText'
import { withoutCmsBlocks } from '../../utils/withoutCmsBlocks'

const WebinarDetailsPage = () => {
    const { webinarId } = Route.useParams()
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()
    const { isAuthenticated } = useAuth()

    const { data: webinar, isLoading } = api.webinar.getPublic.useQuery({
        purchasableItemId: webinarId,
    })
    const { data: bookedItemIds } = api.appointment.getBookedSlotItemIds.useQuery(undefined, {
        enabled: isAuthenticated,
    })

    const hasAttachedBlocks = useAttachedBlocks(webinar?.cmsBlocks).length > 0
    const longDescriptionHtml = webinar?.longDescription
        ? getLocalizedValue(webinar.longDescription)
        : ''

    return (
        <CheckoutLayout>
            <section className="mx-auto w-full max-w-4xl px-6 py-16">
                <Link
                    className="mb-8 inline-flex items-center gap-1 text-sm font-medium text-brand-gray transition-colors hover:text-primary"
                    to="/webinars"
                >
                    <ChevronLeft className="h-4 w-4" />
                    {t('frontend.catalog.backToWebinars')}
                </Link>

                {isLoading ? (
                    <div>
                        <Skeleton className="mb-4 h-8 w-64" />
                        <Skeleton className="mb-8 h-6 w-32" />
                        <Skeleton className="h-40 w-full" />
                    </div>
                ) : !webinar ? (
                    <div className="py-24 text-center">
                        <p className="text-lg text-brand-gray">
                            {t('frontend.catalog.webinarNotFound')}
                        </p>
                    </div>
                ) : (
                    <div>
                        <h1 className="mb-2 text-4xl font-bold text-brand-dark">
                            {getLocalizedValue(webinar.name)}
                        </h1>
                        {webinar.expertName && (
                            <p className="mb-6 text-lg text-brand-gray">{webinar.expertName}</p>
                        )}

                        <div className="mb-8 flex flex-col gap-1">
                            {webinar.startDate && (
                                <span className="text-brand-gray">
                                    {formatDateWithTime(webinar.startDate)}
                                </span>
                            )}
                            {webinar.listPrice != null && (
                                <span className="text-2xl font-bold text-primary">
                                    {formatPrice(webinar.listPrice)}
                                </span>
                            )}
                            <span className="text-brand-gray">
                                {webinar.freePlaces > 0
                                    ? `${t('frontend.catalog.freePlaces')}: ${webinar.freePlaces}`
                                    : t('frontend.catalog.soldOut')}
                            </span>
                        </div>

                        <div className="mb-10">
                            <WebinarCtaButton
                                purchasableItemId={webinar.purchasableItemId}
                                isBooked={(bookedItemIds ?? []).includes(webinar.purchasableItemId)}
                                freePlaces={webinar.freePlaces}
                                size="lg"
                            />
                        </div>

                        {(hasAttachedBlocks || longDescriptionHtml) && (
                            <div className="border-t border-slate-100 pt-8">
                                <h2 className="mb-4 text-xl font-bold text-brand-dark">
                                    {t('frontend.catalog.description')}
                                </h2>
                                <AttachedBlocks blocks={webinar.cmsBlocks} className="mb-6" />
                                {longDescriptionHtml && (
                                    <div
                                        className={longFormRichTextClassName}
                                        dangerouslySetInnerHTML={{ __html: longDescriptionHtml }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </section>
        </CheckoutLayout>
    )
}

export const Route = createFileRoute('/webinars/$webinarId')({
    component: WebinarDetailsPage,
    loader: async ({ context, params }) => {
        const [webinar, websiteSettings] = await Promise.allSettled([
            context.apiUtils.webinar.getPublic.ensureData({ purchasableItemId: params.webinarId }),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
        ])
        return {
            webinar:
                webinar.status === 'fulfilled' && webinar.value
                    ? withoutCmsBlocks(webinar.value)
                    : null,
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
            webinarId: params.webinarId,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: loaderData?.webinar?.ogTitle || 'Long-Y - Webinárium',
            description: loaderData?.webinar?.ogDescription,
            imageId: loaderData?.webinar?.ogImageId,
            url: `${import.meta.env.VITE_WEB_URL || ''}/webinars/${loaderData?.webinarId ?? ''}`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
